import api from './api';

// Enums
export enum AccountGroup {
  Assets = 1,
  Liabilities = 2,
  Equity = 3,
  Income = 4,
  Expenses = 5,
  OffBalanceSheet = 6
}

export enum AccountType {
  Asset = 1,
  Liability = 2,
  Equity = 3,
  Income = 4,
  Expense = 5
}

// باید دقیقاً با VoucherType بک‌اند یکی باشد (Backend/FarazERP.Domain/Accounting/VoucherType.cs)
export enum VoucherType {
  Manual = 0,
  Treasury = 1,
  Expense = 2,
  Payroll = 3,
  Opening = 4,
  Closing = 5,
  IncomeStatement = 6
}

export enum ReportType {
  TrialBalance = 1,
  BalanceSheet = 2,
  IncomeStatement = 3,
  CashFlow = 4,
  GeneralLedger = 5,
  SubsidiaryLedger = 6,
  AccountStatement = 7
}

// DTOs
export interface ChartOfAccountDto {
  id: number;
  code: string;
  name: string;
  description?: string;
  group: AccountGroup;
  type: AccountType;
  parentId?: number;
  parentName?: string;
  level: number;
  isActive: boolean;
  isSystem: boolean;
  isTemporary: boolean;
  isOffBalanceSheet: boolean;
  isFloatingCenterAcceptable: boolean;
  isMultiCurrency: boolean;
  isAutoGenerated: boolean;
  // حساب در آرتیکل اسناد استفاده شده — چنین حسابی نباید زیرمجموعه بگیرد
  hasVoucherLines?: boolean;
  externalId?: string;
  source?: string;
  createdAt: string;
  updatedAt?: string;
  parent?: ChartOfAccountDto;
  children: ChartOfAccountDto[];
}

export interface FiscalYearDto {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  isClosed: boolean;
  isCurrent: boolean;
  description?: string;
  createdAt: string;
  updatedAt?: string;
  settings?: AccountingSettingsDto;
  vouchers?: VoucherDto[];
}

export interface AccountingSettingsDto {
  id: number;
  profitLossAccountId?: number;
  profitLossAccountName?: string;
  retainedEarningsAccountId?: number;
  retainedEarningsAccountName?: string;
  openingVoucherId?: number;
  closingVoucherId?: number;
  incomeStatementVoucherId?: number;
  finalizationDate?: string;
  createdAt: string;
  updatedAt?: string;
  fiscalYear?: FiscalYearDto;
  profitLossAccount?: ChartOfAccountDto;
  retainedEarningsAccount?: ChartOfAccountDto;
  openingVoucher?: VoucherDto;
  closingVoucher?: VoucherDto;
  incomeStatementVoucher?: VoucherDto;
}

export interface VoucherLineDto {
  id: number;
  voucherId: number;
  chartOfAccountId: number;
  chartOfAccountName?: string;
  chartOfAccountCode?: string;
  debit: number;
  credit: number;
  foreignDebit?: number;
  foreignCredit?: number;
  currencyId?: number;
  description?: string;
  lineNumber: number;
  floatingCenterCode?: string;
  floatingCenterName?: string;
  voucher?: VoucherDto;
  chartOfAccount?: ChartOfAccountDto;
}

export interface VoucherAuditLogDto {
  id: number;
  voucherId: number;
  userId: number;
  userName?: string;
  action: string;
  actionDate: string;
  description?: string;
  voucher?: VoucherDto;
}

export interface VoucherDto {
  id: number;
  number: string;
  date: string;
  description?: string;
  type: VoucherType;
  fiscalYearId: number;
  fiscalYearName?: string;
  totalDebit: number;
  totalCredit: number;
  isPosted: boolean;
  postedDate?: string;
  referenceNumber?: string;
  source?: string;
  sourceId?: number;
  isSystem: boolean;
  isVoided: boolean;
  voidedDate?: string;
  voidedReason?: string;
  isApproved: boolean;
  approvedBy?: number;
  approvedDate?: string;
  isFinalized: boolean;
  finalizedBy?: number;
  finalizedDate?: string;
  createdAt: string;
  updatedAt?: string;
  fiscalYear?: FiscalYearDto;
  voucherLines: VoucherLineDto[];
  auditLogs: VoucherAuditLogDto[];
}

export interface FinancialReportDto {
  id: number;
  type: ReportType;
  fiscalYearId: number;
  fiscalYearName?: string;
  startDate?: string;
  endDate?: string;
  title?: string;
  description?: string;
  reportData?: string;
  isDraft: boolean;
  generatedAt: string;
  finalizedAt?: string;
  generatedBy?: number;
  createdAt: string;
  updatedAt?: string;
}

// یک گره تراز آزمایشی سلسله‌مراتبی — مانده شامل جمع زیرمجموعه‌هاست
export interface TrialBalanceRowDto {
  accountId: number;
  code?: string;
  name?: string;
  level: number;
  parentId?: number | null;
  hasChildren: boolean;
  openingDebit: number;   // گردش قبل از بازه
  openingCredit: number;
  openingBalance: number; // مانده اول دوره
  debit: number;          // گردش دوره
  credit: number;
  balance: number;        // مانده آخر دوره
  fiscalYear?: FiscalYearDto;
}

// یک ردیف دفتر حساب — مانده تجمعی از اول بازه
export interface AccountLedgerRowDto {
  voucherLineId: number;
  voucherId: number;
  voucherNumber?: string;
  voucherDate: string;
  lineNumber: number;
  description?: string;
  floatingCenterCode?: string;
  floatingCenterName?: string;
  debit: number;
  credit: number;
  balance: number;
  isPosted: boolean;
}

// یک ردیف دفتر روزنامه قانونی — قالب سامانه مودیان
export interface LegalJournalRowDto {
  rowIndex: number;
  voucherId: number;
  voucherNumber?: string;
  voucherDate: string;
  kolCode?: string;
  kolName?: string;
  moinCode?: string;
  moinName?: string;
  tafsilCode?: string;
  tafsilName?: string;
  description?: string;
  debit: number;
  credit: number;
  isPosted: boolean;
}

// API Service
const accountingService = {
  // ChartOfAccounts
  getAllChartOfAccounts: () => api.get<ChartOfAccountDto[]>('/accounting/chart-of-accounts'),
  getChartOfAccountById: (id: number) => api.get<ChartOfAccountDto>(`/accounting/chart-of-accounts/${id}`),
  getChartOfAccountsByParentId: (parentId?: number) => 
    api.get<ChartOfAccountDto[]>(`/accounting/chart-of-accounts/by-parent/${parentId ?? 'null'}`),
  createChartOfAccount: (data: ChartOfAccountDto) => api.post<ChartOfAccountDto>('/accounting/chart-of-accounts', data),
  updateChartOfAccount: (id: number, data: ChartOfAccountDto) => api.put<ChartOfAccountDto>(`/accounting/chart-of-accounts/${id}`, data),
  deleteChartOfAccount: (id: number) => api.delete(`/accounting/chart-of-accounts/${id}`),

  // FiscalYears
  getAllFiscalYears: () => api.get<FiscalYearDto[]>('/accounting/fiscal-years'),
  getFiscalYearById: (id: number) => api.get<FiscalYearDto>(`/accounting/fiscal-years/${id}`),
  createFiscalYear: (data: FiscalYearDto) => api.post<FiscalYearDto>('/accounting/fiscal-years', data),
  updateFiscalYear: (id: number, data: FiscalYearDto) => api.put<FiscalYearDto>(`/accounting/fiscal-years/${id}`, data),
  deleteFiscalYear: (id: number) => api.delete(`/accounting/fiscal-years/${id}`),
  closeFiscalYear: (id: number) => api.post(`/accounting/fiscal-years/${id}/close`),
  getCurrentFiscalYear: () => api.get<FiscalYearDto>('/accounting/fiscal-years/current'),
  setCurrentFiscalYear: (id: number) => api.post(`/accounting/fiscal-years/${id}/set-current`),

  // Vouchers
  getAllVouchers: () => api.get<VoucherDto[]>('/accounting/vouchers'),
  getVoucherById: (id: number) => api.get<VoucherDto>(`/accounting/vouchers/${id}`),
  getVouchersByFiscalYear: (fiscalYearId: number) => 
    api.get<VoucherDto[]>(`/accounting/vouchers/by-fiscal-year/${fiscalYearId}`),
  getNextVoucherNumber: (fiscalYearId?: number) =>
    api.get<{ number: string }>('/accounting/vouchers/next-number', { params: { fiscalYearId } }),
  createVoucher: (data: VoucherDto) => api.post<VoucherDto>('/accounting/vouchers', data),
  updateVoucher: (id: number, data: VoucherDto) => api.put<VoucherDto>(`/accounting/vouchers/${id}`, data),
  deleteVoucher: (id: number) => api.delete(`/accounting/vouchers/${id}`),
  postVoucher: (id: number) => api.post(`/accounting/vouchers/${id}/post`),
  voidVoucher: (id: number, reason: string) => 
    api.post(`/accounting/vouchers/${id}/void`, { reason }),
  approveVoucher: (id: number) => api.post(`/accounting/vouchers/${id}/approve`),
  finalizeVoucher: (id: number) => api.post(`/accounting/vouchers/${id}/finalize`),
  unpostVoucher: (id: number) => api.post(`/accounting/vouchers/${id}/unpost`),
  getLastFinalizedDate: (fiscalYearId: number) =>
    api.get<{ date: string | null }>(`/accounting/vouchers/last-finalized/${fiscalYearId}`),
  finalizeVouchersUntil: (fiscalYearId: number, untilDate: string) =>
    api.post<{ count: number }>(`/accounting/vouchers/finalize-range`, { fiscalYearId, untilDate }),
  getDashboardSummary: (fiscalYearId: number, includeDraft = false) =>
    api.get<DashboardSummaryDto>(`/accounting/financial-reports/dashboard-summary`, { params: { fiscalYearId, includeDraft } }),

  // FinancialReports
  getAllFinancialReports: () => api.get<FinancialReportDto[]>('/accounting/financial-reports'),
  // تراز آزمایشی سلسله‌مراتبی — کل درخت حساب‌ها با مانده تجمیعی
  getTrialBalanceTree: (fiscalYearId: number, startDate?: string, endDate?: string, includeDraft?: boolean) =>
    api.get<TrialBalanceRowDto[]>('/accounting/financial-reports/trial-balance-tree', {
      params: { fiscalYearId, startDate, endDate, includeDraft },
    }),
  // دفتر حساب — گردش حساب برگ با مانده تجمعی
  getAccountLedger: (chartOfAccountId: number, fiscalYearId: number, startDate?: string, endDate?: string, includeDraft?: boolean) =>
    api.get<AccountLedgerRowDto[]>('/accounting/financial-reports/account-ledger', {
      params: { chartOfAccountId, fiscalYearId, startDate, endDate, includeDraft },
    }),
  // دفتر روزنامه قانونی — قالب سامانه دفاتر تجاری الکترونیکی (مودیان)
  getLegalJournal: (fiscalYearId: number, startDate?: string, endDate?: string, includeDraft?: boolean) =>
    api.get<LegalJournalRowDto[]>('/accounting/financial-reports/legal-journal', {
      params: { fiscalYearId, startDate, endDate, includeDraft },
    }),
  getFinancialReportById: (id: number) => api.get<FinancialReportDto>(`/accounting/financial-reports/${id}`),
  getReportsByFiscalYear: (fiscalYearId: number) => 
    api.get<FinancialReportDto[]>(`/accounting/financial-reports/by-fiscal-year/${fiscalYearId}`),
  getReportsByType: (type: ReportType) => 
    api.get<FinancialReportDto[]>(`/accounting/financial-reports/by-type/${type}`),
  generateTrialBalance: (fiscalYearId: number, startDate?: string, endDate?: string) =>
    api.post<FinancialReportDto>('/accounting/financial-reports/trial-balance', {
      fiscalYearId,
      startDate,
      endDate
    }),
  generateBalanceSheet: (fiscalYearId: number, endDate?: string) =>
    api.post<FinancialReportDto>('/accounting/financial-reports/balance-sheet', {
      fiscalYearId,
      endDate
    }),
  generateIncomeStatement: (fiscalYearId: number, startDate?: string, endDate?: string) =>
    api.post<FinancialReportDto>('/accounting/financial-reports/income-statement', {
      fiscalYearId,
      startDate,
      endDate
    }),
  generateAccountStatement: (chartOfAccountId: number, startDate: string, endDate: string) =>
    api.post<FinancialReportDto>('/accounting/financial-reports/account-statement', {
      chartOfAccountId,
      startDate,
      endDate
    }),
  createFinancialReport: (data: FinancialReportDto) => 
    api.post<FinancialReportDto>('/accounting/financial-reports', data),
  updateFinancialReport: (id: number, data: FinancialReportDto) => 
    api.put<FinancialReportDto>(`/accounting/financial-reports/${id}`, data),
  deleteFinancialReport: (id: number) => api.delete(`/accounting/financial-reports/${id}`),
  finalizeReport: (id: number) => api.post(`/accounting/financial-reports/${id}/finalize`),
};

export default accountingService;

// ─── AccountMapping ───────────────────────────────────────────────────────────

export interface AccountMappingDto {
  id: number;
  entityType: string;
  accountType: string;
  parentChartOfAccountId: number;
  parentChartOfAccountName?: string;
  codePrefix?: string;
  isActive: boolean;
  floatingCenterSourceField?: string;
  floatingCenterEntityType?: string;
}

export interface CreateAccountMappingDto {
  entityType: string;
  accountType: string;
  parentChartOfAccountId: number;
  codePrefix?: string;
  isActive: boolean;
  floatingCenterSourceField?: string;
  floatingCenterEntityType?: string;
}

// ─── PayrollItemMapping ───────────────────────────────────────────────────────

export interface PayrollItemMappingDto {
  id: number;
  payrollItemTemplateId: number;
  payrollItemTemplateName?: string;
  debitChartOfAccountId: number;
  debitChartOfAccountName?: string;
  debitChartOfAccountCode?: string;
  creditChartOfAccountId: number;
  creditChartOfAccountName?: string;
  creditChartOfAccountCode?: string;
  debitFloatingCenterEntityType?: string;
  creditFloatingCenterEntityType?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreatePayrollItemMappingDto {
  payrollItemTemplateId: number;
  debitChartOfAccountId: number;
  creditChartOfAccountId: number;
  debitFloatingCenterEntityType?: string;
  creditFloatingCenterEntityType?: string;
  isActive: boolean;
}

export interface UpdatePayrollItemMappingDto {
  debitChartOfAccountId: number;
  creditChartOfAccountId: number;
  debitFloatingCenterEntityType?: string;
  creditFloatingCenterEntityType?: string;
  isActive: boolean;
}

// ─── Mapping API Service ──────────────────────────────────────────────────────

export const accountMappingService = {
  // AccountMapping CRUD
  getAll: () => api.get<AccountMappingDto[]>('/accounting/mappings'),
  getById: (id: number) => api.get<AccountMappingDto>(`/accounting/mappings/${id}`),
  getByEntityType: (entityType: string) =>
    api.get<AccountMappingDto[]>(`/accounting/mappings/by-entity-type/${entityType}`),
  create: (data: CreateAccountMappingDto) =>
    api.post<AccountMappingDto>('/accounting/mappings', data),
  update: (id: number, data: CreateAccountMappingDto) =>
    api.put<AccountMappingDto>(`/accounting/mappings/${id}`, data),
  delete: (id: number) => api.delete(`/accounting/mappings/${id}`),

  // PayrollItemMapping CRUD
  getAllPayrollItems: () =>
    api.get<PayrollItemMappingDto[]>('/accounting/mappings/payroll-items'),
  getPayrollItemById: (id: number) =>
    api.get<PayrollItemMappingDto>(`/accounting/mappings/payroll-items/${id}`),
  getPayrollItemByTemplateId: (templateId: number) =>
    api.get<PayrollItemMappingDto>(`/accounting/mappings/payroll-items/by-template/${templateId}`),
  createPayrollItem: (data: CreatePayrollItemMappingDto) =>
    api.post<PayrollItemMappingDto>('/accounting/mappings/payroll-items', data),
  updatePayrollItem: (id: number, data: UpdatePayrollItemMappingDto) =>
    api.put<PayrollItemMappingDto>(`/accounting/mappings/payroll-items/${id}`, data),
  deletePayrollItem: (id: number) =>
    api.delete(`/accounting/mappings/payroll-items/${id}`),

  // Auto-Voucher Generation
  generatePayrollVoucher: (payrollHeaderId: number, fiscalYearId: number, createdByUserId: number) =>
    api.post(`/accounting/mappings/generate-voucher/payroll/${payrollHeaderId}`, null, {
      params: { fiscalYearId, createdByUserId }
    }),
  generateExpenseVoucher: (expenseId: number, fiscalYearId: number, createdByUserId: number) =>
    api.post(`/accounting/mappings/generate-voucher/expense/${expenseId}`, null, {
      params: { fiscalYearId, createdByUserId }
    }),
  generateContractProgressVoucher: (contractProgressId: number, fiscalYearId: number, createdByUserId: number) =>
    api.post(`/accounting/mappings/generate-voucher/contract-progress/${contractProgressId}`, null, {
      params: { fiscalYearId, createdByUserId }
    }),
  generateTreasuryVoucher: (transactionId: number, fiscalYearId: number, createdByUserId: number) =>
    api.post(`/accounting/mappings/generate-voucher/treasury/${transactionId}`, null, {
      params: { fiscalYearId, createdByUserId }
    }),

  // Auto-Create Helpers
  ensureFloatingCenter: (entityType: string, entityId: number, entityName: string, groupCode: number) =>
    api.post<number>('/accounting/mappings/ensure-floating-center', {
      entityType, entityId, entityName, groupCode
    }),
  ensureDetailCode: (entityType: string, entityId: number, entityName: string, groupCode: number) =>
    api.post<string>('/accounting/mappings/ensure-detail-code', {
      entityType, entityId, entityName, groupCode
    }),
};

// خلاصه داشبورد مالی
export interface DashboardSummaryDto {
  cashBalance: number;
  receivables: number;
  payables: number;
  inventory: number;
  revenue: number;
  expense: number;
  netIncome: number;
  currentRatio: number | null;
  quickRatio: number | null;
  cashRatio: number | null;
  debtRatio: number | null;
  cashFlow: { month: number; value1: number; value2: number; value3: number }[];
  incomeExpense: { month: number; value1: number; value2: number; value3: number }[];
  expenseBreakdown: { name: string; amount: number }[];
  assetBreakdown: { name: string; amount: number }[];
  workingCapital: number;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  retainedEarnings: number;
  ebit: number;
  workingCapitalGap: number;
  altmanZ: number | null;
  altmanZone: 'safe' | 'grey' | 'distress' | null;
  ohlsonProbability: number | null;
  zmijewskiProbability: number | null;
  payroll: {
    activeEmployees: number;
    lastPeriodTitle: string | null;
    lastPeriodGross: number;
    lastPeriodNet: number;
    lastPeriodTax: number;
    lastPeriodInsurance: number;
    monthlyTrend: { month: number; value1: number; value2: number; value3: number }[];
    monthlyLabels: string[];
  } | null;
}
